import { api } from '@/lib/api';

export type WebsiteAnalyticsDashboard = {
  generatedAt: string;
  hospitalId: string;
  /** Latest pageview stored for this hospital (any time). */
  lastEventAt: string | null;
  period: {
    days: number;
    since: string;
    todayIst: string;
  };
  domainFilter: string | null;
  domains: string[];
  summary: {
    pageviews: number;
    uniqueVisitors: number;
    sessions: number;
    pagesPerSession: number;
    bounceRatePct: number;
    newVisitors: number;
    returningVisitors: number;
    avgLoadTimeMs: number | null;
    avgTtfbMs: number | null;
    websiteLeads: number;
    leadConversionPct: number;
  };
  dailyTrend: Array<{
    date: string;
    pageviews: number;
    uniqueVisitors: number;
    sessions: number;
  }>;
  topPages: Array<{
    pagePath: string;
    pageviews: number;
    uniqueVisitors: number;
    avgLoadTimeMs: number | null;
  }>;
  topReferrers: Array<{ referrer: string; pageviews: number }>;
  utmSources: Array<{
    utmSource: string;
    utmMedium: string;
    pageviews: number;
    sessions: number;
  }>;
  devices: Array<{ deviceType: string; pageviews: number }>;
  slowPages: Array<{
    pagePath: string;
    samples: number;
    avgLoadTimeMs: number;
    avgTtfbMs: number | null;
  }>;
  whatsApp?: {
    summary: {
      sessions: number;
      pageviews: number;
      uniqueVisitors: number;
      bookAppointmentPageviews: number;
      opdBookingsFromWelcome: number;
      opdBookingsWithWhatsappUtm: number;
    };
    byLink: Array<{
      utmContent: string;
      utmCampaign: string;
      sessions: number;
      pageviews: number;
    }>;
    journeys: Array<{
      sessionId: string;
      visitorId: string;
      startedAt: string;
      utmCampaign: string | null;
      utmContent: string | null;
      pagePaths: string[];
      reachedBookAppointments: boolean;
    }>;
    bookings: Array<{
      leadId: string;
      patientName: string;
      phone: string;
      websiteOpdBookedAt: string;
      websiteOpdBookingChannel: string | null;
      utmSource: string | null;
      utmMedium: string | null;
      utmContent: string | null;
      googleAdsUtmCampaign: string | null;
      campaignWelcomeWaSent: boolean;
    }>;
  };
};

export type WebsiteLeadListItem = {
  id: string;
  patientName: string;
  phone: string;
  email: string | null;
  status: string;
  leadSubStatus: string | null;
  createdAt: string;
  sourceDetails: string | null;
  campaignName: string | null;
  specialisation: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  assignedTo: { id: string; name: string } | null;
};

export type WebsiteLeadsResponse = {
  period: { days: number; since: string };
  total: number;
  items: WebsiteLeadListItem[];
};

type ApiEnvelope<T> = { data: T; message?: string };

export const websiteAnalyticsApi = {
  getDashboard(params?: { days?: number; domain?: string | null }) {
    return api
      .get<ApiEnvelope<WebsiteAnalyticsDashboard>>('/api/website-analytics/dashboard', {
        params: {
          days: params?.days,
          domain: params?.domain || undefined,
        },
      })
      .then((r) => r.data.data);
  },

  getWebsiteLeads(params?: { days?: number }) {
    return api
      .get<ApiEnvelope<WebsiteLeadsResponse>>('/api/website-analytics/website-leads', {
        params: { days: params?.days },
      })
      .then((r) => r.data.data);
  },
};
